import { useEffect, useMemo, useRef, useState } from 'react';
import { exportRanges } from '../core/clips';
import { estimateBytes, formatBytes, outputGeometry, type Framing, type OutputFormat, type Quality } from '../core/format';
import { formatEta, formatTotal, formatVideoTime } from '../core/time';
import { holdWakeLock, pickSaveTarget, startExport, type ExportHandle } from '../export/client';
import { useThumbnails } from '../media/thumbs';
import { useReel } from '../state/reel';
import { useApp } from '../state/store';
import { Button, Chip, Field, Icon, Panel, Progress, ScoreBug, Segmented } from '../ui';

type Run =
  | { status: 'idle' }
  | { status: 'running'; done: number; total: number; clip: number; clipCount: number; bytes: number; startedAt: number; name: string; codec: string | null }
  | { status: 'done'; bytes: number; seconds: number; name: string; blobUrl: string | null }
  | { status: 'error'; message: string }
  | { status: 'canceled' };

const FORMATS: { id: OutputFormat; title: string; hint: string; glyph: [number, number] }[] = [
  { id: 'h', title: 'Horizontal 16:9', hint: 'YouTube, TV, laptops', glyph: [36, 20] },
  { id: 'v', title: 'Vertical 9:16', hint: 'Reels, TikTok, Shorts, status', glyph: [22, 38] },
  { id: 's', title: 'Square 1:1', hint: 'Instagram feed, posts', glyph: [30, 30] },
];

export function ExportScreen() {
  const { project, updateSettings, setStep, file, info } = useApp();
  const { clips, stats } = useReel();
  const s = project.settings;
  const [run, setRun] = useState<Run>({ status: 'idle' });
  const handle = useRef<ExportHandle | null>(null);
  const firstIncluded = clips.find((c) => c.included);
  const thumbs = useThumbnails(file, firstIncluded ? [firstIncluded.start] : []);
  const thumb = firstIncluded ? thumbs.get(Math.round(firstIncluded.start)) : undefined;
  const srcW = info?.width ?? 1920;
  const srcH = info?.height ?? 1080;
  const geometry = useMemo(() => outputGeometry(srcW, srcH, s.format, s.framing, s.cropPos, s.quality), [srcW, srcH, s.format, s.framing, s.cropPos, s.quality]);
  const estimate = estimateBytes(geometry.width, geometry.height, stats.total);
  const canStream = typeof window.showSaveFilePicker === 'function';
  const base = (file?.name ?? 'match').replace(/\.[^.]+$/, '');
  const suggested = `${base}-highlights${s.format === 'v' ? '-vertical' : s.format === 's' ? '-square' : ''}.mp4`;

  useEffect(() => {
    if (run.status !== 'running') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [run.status]);

  useEffect(() => () => handle.current?.cancel(), []);

  const start = async () => {
    if (!file || !info) return;
    const target = await pickSaveTarget(suggested);
    if (target === 'aborted') return;
    const ranges = exportRanges(clips);
    const name = target ? target.name : suggested;
    const startedAt = performance.now();
    const total = ranges.reduce((a, r) => a + r.end - r.start, 0);
    setRun({ status: 'running', done: 0, total, clip: 0, clipCount: ranges.length, bytes: 0, startedAt, name, codec: null });
    useApp.setState({ exporting: true });
    const release = await holdWakeLock();
    const h = startExport({ file, ranges, geometry, handle: target }, (msg) => {
      if (msg.type === 'progress') setRun((r) => (r.status === 'running' ? { ...r, done: msg.done, clip: msg.clip, bytes: msg.bytes } : r));
      if (msg.type === 'ready') setRun((r) => (r.status === 'running' ? { ...r, codec: msg.videoCodec } : r));
    });
    handle.current = h;
    try {
      const result = await h.done;
      if (result === 'canceled') setRun({ status: 'canceled' });
      else setRun({ status: 'done', bytes: result.bytes, seconds: (performance.now() - startedAt) / 1000, name, blobUrl: result.blob ? URL.createObjectURL(result.blob) : null });
    } catch (e) {
      setRun({ status: 'error', message: e instanceof Error ? e.message : String(e) });
    } finally {
      release();
      handle.current = null;
      useApp.setState({ exporting: false });
    }
  };

  if (run.status === 'running') {
    const pct = run.total ? (run.done / run.total) * 100 : 0;
    const elapsed = (performance.now() - run.startedAt) / 1000;
    const left = run.done > 1 ? (elapsed * (run.total - run.done)) / run.done : NaN;
    const included = clips.filter((c) => c.included);
    const current = Math.max(1, run.clip);
    const from = Math.max(0, Math.min(current - 4, included.length - 8));
    const windowed = included.slice(from, from + 8);
    const cur = included[current - 1];
    return (
      <Panel className="exporting" aria-labelledby="exporting-title" style={{ gap: 26 }}>
        <div className="app-row-between" style={{ alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {cur && <ScoreBug period={cur.period ? (cur.period === 1 ? '1st' : '2nd') : undefined} time={formatVideoTime(cur.start)} label={`Clip ${current} / ${run.clipCount}`} />}
            <h1 id="exporting-title" className="display-lg caps" style={{ margin: 0 }}>
              Exporting your reel
            </h1>
          </div>
          <Button icon="x" onClick={() => handle.current?.cancel()}>
            Cancel export
          </Button>
        </div>
        <Progress value={pct} label="Export progress" detail={`${run.done > 1 ? formatEta(left) : 'Starting'} · ${formatTotal(elapsed)} elapsed`} />
        <ol className="status-list">
          {windowed.map((c, i) => {
            const n = from + i + 1;
            const state = n < current ? 'done' : n === current ? 'now' : 'wait';
            return (
              <li key={c.id} className="status-item" style={{ background: state === 'now' ? 'var(--signal-soft)' : undefined }}>
                <span className="status-dot" style={state === 'done' ? { background: 'var(--pitch)', borderColor: 'var(--pitch)', color: 'var(--on-pitch)' } : state === 'now' ? { background: 'var(--signal)', borderColor: 'var(--signal)', color: 'var(--on-signal)' } : undefined}>
                  {state === 'done' && <Icon name="check" size={14} strokeWidth={2.5} />}
                  {state === 'now' && <Icon name="loader" size={14} strokeWidth={2.5} className="spin" />}
                </span>
                <span style={{ flexGrow: 1, fontSize: 14, fontWeight: state === 'now' ? 600 : 400, color: state === 'wait' ? 'var(--ink-muted)' : undefined }}>
                  {c.title} <span className="time text-small muted">· {formatVideoTime(c.start)}</span>
                </span>
                <span className="text-small" style={{ color: state === 'wait' ? 'var(--ink-muted)' : undefined }}>
                  {state === 'done' ? 'Done' : state === 'now' ? 'Encoding' : 'Waiting'}
                </span>
              </li>
            );
          })}
        </ol>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
          <div className="note-box">
            <Icon name="monitor" size={18} />
            Your screen stays awake until the export is done.
          </div>
          <div className="note-box">
            <Icon name="info" size={18} />
            Keep this tab open. Closing it stops the export.
          </div>
        </div>
        <div className="app-row" style={{ paddingTop: 18, borderTop: '1px solid var(--line)' }}>
          <span style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--radius-sm)', background: 'var(--signal-soft)', color: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="download" />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="time">{run.name}</span>
            <span className="text-small muted">
              {geometry.width} × {geometry.height}
              {run.codec ? ` · ${run.codec.toUpperCase()}` : ''}
              {run.bytes > 0 ? ` · ${formatBytes(run.bytes)} written so far` : canStream ? '' : ' · built in memory, saved at the end'}
            </span>
          </div>
        </div>
      </Panel>
    );
  }

  if (run.status === 'done') {
    return (
      <Panel className="exporting" aria-labelledby="done-title" style={{ gap: 22 }}>
        <span style={{ alignSelf: 'flex-start' }}>
          <Chip tone="pitch" icon="check">
            Done
          </Chip>
        </span>
        <h1 id="done-title" className="display-lg caps" style={{ margin: 0 }}>
          Your reel is ready
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          {stats.count} {stats.count === 1 ? "clip" : "clips"}, {formatTotal(stats.total)} long, {formatBytes(run.bytes)}. Made in {formatTotal(run.seconds)} without leaving this computer.
        </p>
        {run.blobUrl ? (
          <a className="hl-btn hl-btn-primary hl-btn-lg" href={run.blobUrl} download={run.name} style={{ alignSelf: 'flex-start' }}>
            <Icon name="download" size={18} />
            Save {run.name}
          </a>
        ) : (
          <p className="app-row" style={{ margin: 0 }}>
            <Icon name="check" />
            Saved as <span className="time">{run.name}</span> where you chose.
          </p>
        )}
        <div className="app-row">
          <Button icon="arrow-left" onClick={() => setRun({ status: 'idle' })}>
            Export another version
          </Button>
          <Button onClick={() => setStep('clips')}>Back to clips</Button>
        </div>
      </Panel>
    );
  }

  const outW = s.format === 'v' ? 124 : s.format === 's' ? 220 : 392;
  const crop = geometry.crop;
  const winLeft = crop ? (crop.x / srcW) * 100 : 0;
  const winWidth = crop ? (crop.w / srcW) * 100 : 100;

  return (
    <div className="app-grid-side" style={{ gridTemplateColumns: 'minmax(0, 1fr) 456px' }}>
      <Panel aria-labelledby="export-title" style={{ gap: 0, padding: '24px 28px' }}>
        <h1 id="export-title" className="app-title" style={{ marginBottom: 18 }}>
          Export settings
        </h1>
        {run.status === 'error' && (
          <div className="app-banner app-banner-danger" role="alert" style={{ marginBottom: 16 }}>
            <Icon name="alert" />
            The export stopped: {run.message}
          </div>
        )}
        {run.status === 'canceled' && (
          <div className="app-banner app-banner-warning" style={{ marginBottom: 16 }}>
            <Icon name="info" />
            Export canceled. Nothing was saved.
          </div>
        )}
        <fieldset data-tour="format" className="section" style={{ margin: 0, border: 0, borderTop: '1px solid var(--line)' }}>
          <legend className="app-label" style={{ float: 'left', padding: 0 }}>
            Format
          </legend>
          <div className="fmt-grid">
            {FORMATS.map((f) => (
              <label key={f.id} className={`fmt${s.format === f.id ? ' is-on' : ''}`}>
                <input type="radio" name="format" className="hl-sr" checked={s.format === f.id} onChange={() => updateSettings({ format: f.id })} />
                <span className="fmt-glyph">
                  <span style={{ width: f.glyph[0], height: f.glyph[1] }} />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{f.title}</span>
                  <span className="text-small muted">{f.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {s.format !== 'h' && (
          <div className="section" style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 24 }}>
            <div className="hl-field">
              <span className="hl-label">Framing</span>
              <Segmented<Framing>
                label="Framing"
                value={s.framing}
                onChange={(v) => updateSettings({ framing: v })}
                options={[
                  { value: 'crop', label: 'Crop to fill' },
                  { value: 'fit', label: 'Fit with blurred bars' },
                ]}
              />
            </div>
            {s.framing === 'crop' && (
              <div className="hl-field" style={{ flexGrow: 1 }}>
                <label className="hl-label" htmlFor="crop-pos">
                  Crop position
                </label>
                <input id="crop-pos" className="player-range" type="range" min={0} max={1} step={0.01} value={s.cropPos} onChange={(e) => updateSettings({ cropPos: Number(e.target.value) })} style={{ height: 44 }} />
                <span className="time text-small muted app-row-between">
                  <span>Left</span>
                  <span>Centre</span>
                  <span>Right</span>
                </span>
              </div>
            )}
          </div>
        )}
        <div className="section" style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 24 }}>
          <div className="hl-field">
            <span className="hl-label">Quality</span>
            <Segmented<Quality>
              label="Quality"
              value={s.quality}
              onChange={(v) => updateSettings({ quality: v })}
              options={[
                { value: 'orig', label: 'Original' },
                { value: '720', label: '720p' },
              ]}
            />
          </div>
          <span className="text-small muted" style={{ paddingBottom: 12 }}>
            {geometry.width} × {geometry.height} · about {formatBytes(estimate)}
            {s.quality === '720' ? ' · good for messaging apps' : ''}
          </span>
        </div>
        <div className="section" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          <Field label="Before each clip" type="number" min={0} max={30} step={0.5} suffix="s" value={s.padBefore} onChange={(e) => updateSettings({ padBefore: Math.max(0, Number(e.target.value) || 0) })} hint={project.method === 'live' ? 'Timestamp lists only' : undefined} />
          <Field label="After each clip" type="number" min={0} max={30} step={0.5} suffix="s" value={s.padAfter} onChange={(e) => updateSettings({ padAfter: Math.max(0, Number(e.target.value) || 0) })} />
          <Field label="Merge clips closer than" type="number" min={0} max={30} step={0.5} suffix="s" value={s.mergeGap} onChange={(e) => updateSettings({ mergeGap: Math.max(0, Number(e.target.value) || 0) })} />
        </div>
        <div className="section" style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--radius-sm)', background: 'var(--surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="download" />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="time">{suggested}</span>
            <span className="text-small muted">
              {canStream
                ? 'You pick where to save. The file is written straight to disk while it exports, so a long reel never fills your memory.'
                : 'This browser builds the file in memory and saves it at the end. Long reels may not fit; Chrome and Edge write straight to disk.'}
            </span>
          </div>
        </div>
        {!canStream && estimate > 1.5e9 && (
          <div className="app-banner app-banner-warning">
            <Icon name="alert" />
            About {formatBytes(estimate)} is a lot to hold in memory. Choose 720p, or use Chrome or Edge.
          </div>
        )}
        <div data-tour="exportbtn" className="app-row" style={{ paddingTop: 18, borderTop: "1px solid var(--line)", gap: 20 }}>
          <Button variant="primary" size="lg" icon="scissors" disabled={stats.count === 0 || !info} onClick={() => void start()}>
            Export highlight reel
          </Button>
          <span className="time text-small muted">
            {formatTotal(stats.total)} · {stats.count} clips · MP4
          </span>
        </div>
      </Panel>

      <Panel data-tour="preview" aria-labelledby="preview-title" className="app-sticky" style={{ padding: '24px 32px', gap: 14 }}>
        <h2 id="preview-title" className="app-label">
          Framing preview
        </h2>
        <div className="preview-src">
          {thumb ? <img src={thumb} alt="First clip of the reel" /> : null}
          {crop && s.framing === 'crop' && (
            <>
              <span className="preview-dim" style={{ left: 0, width: `${winLeft}%` }} />
              <span className="preview-dim" style={{ left: `${winLeft + winWidth}%`, right: 0 }} />
              <span className="preview-window" style={{ left: `${winLeft}%`, width: `${winWidth}%` }} />
            </>
          )}
          <span style={{ position: 'absolute', left: 8, bottom: 8 }}>
            <ScoreBug label={`Source ${srcW} × ${srcH}`} />
          </span>
        </div>
        {s.format !== 'h' ? (
          <div className="app-row" style={{ alignItems: 'flex-start', gap: 16 }}>
            <div className="preview-out" style={{ width: outW }}>
              {thumb && s.framing === 'crop' && crop && (
                <img src={thumb} alt="" style={{ position: 'absolute', top: 0, height: '100%', width: `${(srcW / crop.w) * 100}%`, maxWidth: 'none', left: `${-(crop.x / crop.w) * 100}%`, objectFit: 'cover' }} />
              )}
              {thumb && s.framing === 'fit' && (
                <>
                  <img src={thumb} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(8px) brightness(0.55)' }} />
                  <img src={thumb} alt="" style={{ position: 'absolute', left: 0, top: '50%', width: '100%', transform: 'translateY(-50%)' }} />
                </>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
              <span className="app-label">Output</span>
              <span className="time">
                {geometry.width} × {geometry.height} · {s.format === 'v' ? '9:16' : '1:1'}
              </span>
              <span className="text-small muted">
                {s.framing === 'fit'
                  ? 'The whole picture in the middle, with a blurred copy filling the space around it.'
                  : crop && crop.w < geometry.width
                    ? `A ${crop.w} px wide slice of the source, scaled up. Sharp on phones, softer than the original.`
                    : 'Cropped from the source around the crop position.'}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-small muted" style={{ margin: 0 }}>
            Horizontal keeps the full frame{geometry.mode === 'direct' ? ', exactly as filmed' : `, scaled to ${geometry.width} × ${geometry.height}`}.
          </p>
        )}
        <p className="text-small muted" style={{ margin: 0, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          The crop position applies to the whole reel. Veo footage already follows the play, so the centre usually works.
        </p>
      </Panel>
    </div>
  );
}
